import Icon from '@atoms/Icon/Icon';
import { IAccountDropdown } from '@models/IAccountDropdown';
import { IMainNavigationItem } from '@models/IHeader';
import ILanguageSelector from '@models/ILanguageSelector';
import { ILink } from '@models/ILink';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Dispatch, SetStateAction, useState } from 'react';
import MobileTopLevel from './MobileTopLevel';

interface Props {
  readonly accountLink?: ILink;
  readonly accountsEnabled: boolean;
  readonly accountDropdownNavigation: IAccountDropdown;
  readonly backLabel: string;
  readonly languageActiveIndex: number;
  readonly languageSelector?: ILanguageSelector;
  readonly mainNavigation: IMainNavigationItem[];
  readonly serviceAccountLink?: ILink;
  readonly showAccountIcon?: boolean;
  readonly onSetIsOpen: Dispatch<SetStateAction<boolean>>;
}

const MobileSubNav = ({
  accountLink,
  accountsEnabled,
  accountDropdownNavigation,
  backLabel,
  languageActiveIndex,
  languageSelector,
  mainNavigation,
  serviceAccountLink,
  showAccountIcon,
  onSetIsOpen,
}: Props) => {
  const isSmallMobileViewport = useMediaQuery(576);
  const isSmallMobileScreen = useMediaQuery(576, BreakpointTypeEnum.Screen);
  const isZoomedDesktop = !isSmallMobileScreen && isSmallMobileViewport;
  const [activeCtaTabIndex, setActiveCtaTabIndex] = useState(-1);
  const [isSecondLevelOpen, setIsSecondLevelOpen] = useState(false);
  const {
    closeMobileNavigationAriaLabel,
    backToFirstLevelNavigationAriaLabel,
  } = useAriaLabels();

  return (
    <FocusTrap
      focusTrapOptions={{
        preventScroll: true,
        escapeDeactivates: false,
      }}
    >
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{
          delay: 0.15,
          duration: 1.1,
          ease: [0.74, 0, 0.19, 1.02],
        }}
        className="fixed top-0 left-0 z-max flex h-full w-screen flex-col overflow-hidden bg-white"
        data-testid="CT-01-Header-Mobile-Sub-Nav"
      >
        <div
          className={classNames(
            'relative flex flex-row items-center px-5 md:pr-8 md:pl-8 lgm:pl-32',
            isZoomedDesktop ? 'pt-2.5 pb-3' : 'pt-8 pb-11'
          )}
        >
          <AnimatePresence>
            {isSecondLevelOpen && (
              <motion.button
                initial={{ x: '-100%', opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: '-100%', opacity: 0 }}
                transition={{
                  delay: 0.15,
                  duration: 1.1,
                  ease: [0.74, 0, 0.19, 1.02],
                }}
                className="absolute flex flex-row"
                onClick={() => {
                  setIsSecondLevelOpen(false);
                  setActiveCtaTabIndex(-1);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsSecondLevelOpen(false);
                    setActiveCtaTabIndex(-1);
                  }
                }}
                aria-label={backToFirstLevelNavigationAriaLabel}
                data-testid="CT-01-Header-Mobile-Sub-Nav-Back-Button"
              >
                <div className="mr-4 flex h-7 w-7 rotate-180 items-center justify-center rounded-full bg-yellow">
                  <Icon name="east" size={IconSizeEnum.sm} />
                </div>
                <p
                  className="flex items-center"
                  data-testid="CT-01-Header-Mobile-Sub-Nav-Back-Button-Label"
                >
                  {backLabel}
                </p>
              </motion.button>
            )}
          </AnimatePresence>

          <button
            className="ml-auto flex justify-end"
            onClick={() => {
              onSetIsOpen((oldState) => !oldState);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onSetIsOpen((oldState) => !oldState);
              }
            }}
            aria-label={closeMobileNavigationAriaLabel}
          >
            <Icon name="close" size={IconSizeEnum.lg} />
          </button>
        </div>

        <div className="grow overflow-y-auto overflow-x-hidden bg-white">
          <MobileTopLevel
            accountDropdownNavigation={accountDropdownNavigation}
            accountLink={accountLink}
            accountsEnabled={accountsEnabled}
            activeCtaTabIndex={activeCtaTabIndex}
            isSecondLevelOpen={isSecondLevelOpen}
            languageActiveIndex={languageActiveIndex}
            languageSelector={languageSelector}
            mainNavigation={mainNavigation}
            serviceAccountLink={serviceAccountLink}
            showAccountIcon={showAccountIcon}
            onSetActiveCtaTabIndex={setActiveCtaTabIndex}
            onSetIsSecondLevelOpen={setIsSecondLevelOpen}
          />
        </div>
      </motion.div>
    </FocusTrap>
  );
};

export default MobileSubNav;
